<template>
  <div class="page-outer">
    <div class="page-header page-header-fixed-form">
      <div class="header-left">
        <h1 class="page-title">{{ pageTitle }}</h1>
      </div>
      <div class="header-actions">
        <button type="button" class="btn btn-secondary" @click="handleBack">
          <i class="ri-arrow-left-line"></i>
          返回列表
        </button>
      </div>
    </div>

    <div class="page-container">
    <div v-if="loading" class="loading-state">
      <div class="spinner"></div>
      <p>載入中...</p>
    </div>

    <div v-else-if="error" class="error-state">
      <p class="error-message">{{ error }}</p>
      <button class="btn btn-secondary" @click="loadBroadcast">重試</button>
    </div>

    <div v-else-if="broadcast" class="detail-container">
      <!-- 基本資訊 -->
      <div class="detail-section" id="section-basic">
        <h3>基本資訊</h3>
        <div class="info-grid">
          <div class="info-item">
            <label>活動名稱</label>
            <div class="info-value">{{ broadcast.campaignName }}</div>
          </div>
          <div class="info-item">
            <label>狀態</label>
            <div class="info-value">
              <span :class="getStatusBadgeClass(broadcast.status)">
                {{ formatStatus(broadcast.status) }}
              </span>
              <p v-if="broadcast.prepareError" class="prepare-error">{{ broadcast.prepareError }}</p>
            </div>
          </div>
          <div class="info-item">
            <label>訊息類型</label>
            <div class="info-value">{{ formatMessageType(broadcast.messageType) }}</div>
          </div>
          <div class="info-item">
            <label>受眾類型</label>
            <div class="info-value">{{ formatAudienceType(broadcast.audienceType) }}</div>
          </div>
          <div class="info-item">
            <label>目標人數</label>
            <div class="info-value">{{ broadcast.targetCount ?? '-' }}</div>
          </div>
          <div class="info-item">
            <label>已發送</label>
            <div class="info-value">{{ broadcast.actualSentCount || 0 }}</div>
          </div>
          <div class="info-item" v-if="broadcast.scheduledAt">
            <label>排程時間</label>
            <div class="info-value">{{ formatDate(broadcast.scheduledAt) }}</div>
          </div>
          <div class="info-item" v-if="broadcast.sentAt">
            <label>發送時間</label>
            <div class="info-value">{{ formatDate(broadcast.sentAt) }}</div>
          </div>
          <div class="info-item">
            <label>建立時間</label>
            <div class="info-value">{{ formatDate(broadcast.createdAt) }}</div>
          </div>
        </div>
      </div>

      <!-- 統計資訊 -->
      <div class="detail-section" v-if="broadcast && ['sending', 'completed', 'failed'].includes(broadcast.status)">
        <div class="stats-section-header">
          <h3>推播統計</h3>
          <div v-if="broadcast.status === 'completed'" class="insight-inline-actions">
            <span v-if="insight && insight.fetchedAt" class="insight-fetched-time">
              <i class="ri-time-line"></i> 成效更新於 {{ formatDate(insight.fetchedAt) }}
            </span>
            <button
              class="btn btn-secondary btn-sm btn-icon"
              :disabled="fetchingInsight"
              @click="handleFetchInsight"
            >
              <i :class="fetchingInsight ? 'ri-loader-4-line spinning' : 'ri-bar-chart-2-line'"></i>
              {{ fetchingInsight ? '查詢中...' : (insight ? '重新查詢成效' : '查詢成效') }}
            </button>
          </div>
          <div v-if="broadcast.status === 'sending' || (broadcast.status === 'completed' && !broadcast.actualSentCount)" class="insight-inline-actions">
            <button
              class="btn btn-warning btn-sm btn-icon"
              :disabled="retrying"
              @click="handleRetryFailed"
            >
              <i :class="retrying ? 'ri-loader-4-line spinning' : 'ri-refresh-line'"></i>
              {{ retrying ? '重試中...' : '重試發送' }}
            </button>
          </div>
        </div>

        <div class="stats-row-label">發送結果</div>
        <div class="stats-grid">
          <div class="stat-card">
            <div class="stat-icon primary">
              <i class="ri-group-line"></i>
            </div>
            <div class="stat-content">
              <div class="stat-label">目標人數</div>
              <div class="stat-value">{{ broadcast.targetCount || 0 }}</div>
            </div>
          </div>
          <div class="stat-card">
            <div class="stat-icon success">
              <i class="ri-mail-send-line"></i>
            </div>
            <div class="stat-content">
              <div class="stat-label">已成功發送</div>
              <div class="stat-value">{{ broadcast.actualSentCount || 0 }}</div>
            </div>
          </div>
          <div class="stat-card">
            <div class="stat-icon danger">
              <i class="ri-close-circle-line"></i>
            </div>
            <div class="stat-content">
              <div class="stat-label">發送失敗</div>
              <div class="stat-value">{{ broadcastFailedCount }}</div>
            </div>
          </div>
          <div class="stat-card">
            <div class="stat-icon warning">
              <i class="ri-time-line"></i>
            </div>
            <div class="stat-content">
              <div class="stat-label">待發送</div>
              <div class="stat-value">{{ broadcastPendingCount }}</div>
            </div>
          </div>
          <div class="stat-card" v-if="broadcast.targetCount">
            <div class="stat-icon info">
              <i class="ri-percent-line"></i>
            </div>
            <div class="stat-content">
              <div class="stat-label">發送進度</div>
              <div class="stat-value">{{ broadcastProgressPct }}%</div>
            </div>
          </div>
        </div>

        <!-- Insight 成效列（單獨一行） -->
        <template v-if="broadcast.status === 'completed'">
          <!-- 有資料 -->
          <div class="stats-row-label insight-row-label">LINE 成效數據</div>
          <div v-if="insight && insight.status === 'ready'" class="stats-grid insight-stats-row">
            <div class="stat-card insight-stat">
              <div class="stat-icon delivered"><i class="ri-send-plane-line"></i></div>
              <div class="stat-content">
                <div class="stat-label">LINE 送達人數</div>
                <div class="stat-value">{{ insight.delivered?.toLocaleString() ?? '-' }}</div>
                <div class="stat-sub" v-if="broadcast.actualSentCount && insight.delivered">{{ insightDeliveryRate }}% 到達率</div>
              </div>
            </div>
            <div class="stat-card insight-stat">
              <div class="stat-icon opened"><i class="ri-eye-line"></i></div>
              <div class="stat-content">
                <div class="stat-label">已開啟（已讀）</div>
                <div class="stat-value">{{ insight.opened?.toLocaleString() ?? '-' }}</div>
                <div class="stat-sub" v-if="insight.delivered && insight.opened">{{ insightOpenRate }}% 開啟率</div>
              </div>
            </div>
            <div class="stat-card insight-stat">
              <div class="stat-icon clicked"><i class="ri-cursor-line"></i></div>
              <div class="stat-content">
                <div class="stat-label">已點擊連結</div>
                <div class="stat-value">{{ insight.clicked?.toLocaleString() ?? '-' }}</div>
                <div class="stat-sub" v-if="insight.delivered && insight.clicked">{{ insightClickRate }}% 點擊率</div>
              </div>
            </div>
          </div>
          <!-- 查詢中 -->
          <div v-else-if="fetchingInsight" class="insight-status-row">
            <i class="ri-loader-4-line spinning"></i> 正在向 LINE Insight API 查詢...
          </div>
          <!-- 資料尚未就緒 -->
          <div v-else-if="insight && insight.status === 'unready'" class="insight-status-row unready">
            <i class="ri-time-line"></i> 成效資料尚未就緒，請於發送隔天（UTC+9）再查詢
          </div>
          <!-- 帳號不支援 -->
          <div v-else-if="insight && insight.status === 'unsupported'" class="insight-status-row unsupported">
            <i class="ri-error-warning-line"></i> 此帳號不支援 Insight API（需認證帳號或優質帳號）
          </div>
        </template>

        <!-- Insight 帳號說明 -->
        <div v-if="broadcast.status === 'completed'" class="insight-account-note">
          <i class="ri-shield-check-line"></i>
          <span>
            LINE Insight 資料通常於發送隔天（UTC+9）才可查詢。
            成效查詢（送達、已讀、點擊）僅支援 <strong>認證帳號</strong> 或 <strong>優質帳號</strong>；
            開發者試用帳號（Developer Trial）及未認證帳號不支援，查詢時將回傳 403 錯誤。
            此外，互動人數不足 20 人時，LINE 會將該數值保護為 null（隱私保護機制）。
          </span>
        </div>

        <!-- 發送進度條 -->
        <div v-if="broadcast.targetCount > 0" class="progress-bar-wrapper">
          <div class="progress-bar-track">
            <div
              class="progress-bar-fill"
              :class="broadcast.status === 'failed' ? 'fill-danger' : 'fill-success'"
              :style="{ width: broadcastProgressPct + '%' }"
            ></div>
          </div>
          <span class="progress-label">{{ broadcast.actualSentCount || 0 }} / {{ broadcast.targetCount }} 人</span>
        </div>

        <!-- 批次失敗明細 -->
        <div v-if="failedBatches.length > 0" class="failed-batches">
          <div class="failed-batches-header">
            <h4><i class="ri-error-warning-line"></i> 失敗批次明細（共 {{ failedBatches.length }} 批，{{ broadcastFailedCount }} 人）</h4>
            <button
              class="btn btn-warning btn-sm btn-icon"
              :disabled="retrying || retryableBatches === 0"
              @click="handleRetryFailed"
            >
              <i :class="retrying ? 'ri-loader-4-line spinning' : 'ri-refresh-line'"></i>
              {{ retrying ? '重試中...' : `重試 ${retryableBatches} 個批次` }}
            </button>
          </div>
          <div class="table-wrapper">
            <table>
              <thead>
                <tr>
                  <th style="width: 80px">批次編號</th>
                  <th style="width: 80px">人數</th>
                  <th>失敗原因</th>
                  <th style="width: 160px">時間</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="batch in failedBatches" :key="batch.id">
                  <td><span class="badge badge-secondary">#{{ batch.batchIndex }}</span></td>
                  <td>{{ batch.batchSize }}</td>
                  <td>
                    <span
                      v-if="batch.errorMessage && batch.errorMessage.startsWith('重試中')"
                      class="error-text retrying"
                    >
                      <i class="ri-refresh-line"></i> {{ batch.errorMessage }}
                    </span>
                    <span v-else class="error-text">
                      <i class="ri-close-circle-line"></i> {{ batch.errorMessage || '未知錯誤' }}
                    </span>
                  </td>
                  <td class="text-muted">{{ formatDate(batch.createdAt) }}</td>
                </tr>
              </tbody>
            </table>
          </div>
          <p v-if="failedBatches.length >= 200" class="hint-text">
            <i class="ri-information-line"></i> 僅顯示前 200 筆失敗批次
          </p>
        </div>
        <div v-else-if="broadcast.status === 'completed'" class="no-errors">
          <i class="ri-checkbox-circle-line"></i> 所有批次發送成功，無失敗紀錄
        </div>
      </div>

      <!-- 訊息內容 -->
      <div class="detail-section" v-if="broadcast.messageContent">
        <h3>訊息內容</h3>
        <div class="message-preview">
          <div v-if="broadcast.messageType === 'text'">
            <pre>{{ broadcast.messageContent.text || JSON.stringify(broadcast.messageContent, null, 2) }}</pre>
          </div>
          <div v-else>
            <pre>{{ JSON.stringify(broadcast.messageContent, null, 2) }}</pre>
          </div>
        </div>
      </div>

      <!-- 受眾設定 -->
      <div class="detail-section" v-if="broadcast.audienceType || broadcast.audienceConfig">
        <h3>受眾設定</h3>
        <div class="info-grid">
          <!-- members：手動選擇會員 -->
          <template v-if="broadcast.audienceType === 'members'">
            <div class="info-item">
              <label>選擇方式</label>
              <div class="info-value">手動選擇</div>
            </div>
            <div class="info-item" v-if="broadcast.audienceConfig && broadcast.audienceConfig.memberIds">
              <label>已選會員數</label>
              <div class="info-value">{{ broadcast.audienceConfig.memberIds.length }} 位</div>
            </div>
            <div class="info-item" v-if="broadcast.targetCount">
              <label>有效 LINE 好友數</label>
              <div class="info-value">{{ broadcast.targetCount }} 位</div>
            </div>
          </template>

          <!-- all：全部好友廣播 -->
          <template v-else-if="broadcast.audienceType === 'all'">
            <div class="info-item">
              <label>發送方式</label>
              <div class="info-value">LINE Broadcast API（發送給所有追蹤者）</div>
            </div>
          </template>

          <!-- rule：受眾規則 -->
          <template v-else-if="broadcast.audienceType === 'rule'">
            <div class="info-item" v-if="broadcast.audienceConfig && broadcast.audienceConfig.ruleId">
              <label>受眾規則 ID</label>
              <div class="info-value">{{ broadcast.audienceConfig.ruleId }}</div>
            </div>
            <div class="info-item" v-if="broadcast.targetCount">
              <label>符合人數</label>
              <div class="info-value">{{ broadcast.targetCount }} 位</div>
            </div>
          </template>

          <!-- tags：標籤篩選 -->
          <template v-else-if="broadcast.audienceType === 'tags'">
            <div class="info-item" v-if="broadcast.audienceConfig && broadcast.audienceConfig.tagNames && broadcast.audienceConfig.tagNames.length">
              <label>標籤</label>
              <div class="info-value">
                <span
                  v-for="tag in broadcast.audienceConfig.tagNames"
                  :key="tag"
                  class="badge badge-secondary"
                  style="margin-right: 8px;"
                >{{ tag }}</span>
              </div>
            </div>
            <div class="info-item" v-if="broadcast.audienceConfig && broadcast.audienceConfig.matchType">
              <label>匹配方式</label>
              <div class="info-value">
                {{ broadcast.audienceConfig.matchType === 'any' ? '任一標籤' : '所有標籤' }}
              </div>
            </div>
            <div class="info-item" v-if="broadcast.targetCount">
              <label>符合人數</label>
              <div class="info-value">{{ broadcast.targetCount }} 位</div>
            </div>
          </template>
        </div>
      </div>

    </div>
    </div>
  </div>
</template>

<script>
import { usePageTitle } from '@/composables/usePageTitle.js';
import lineBroadcastService from '@/api/modules/lineBroadcast.js';
import { showSuccess, showError, showConfirm } from '@/utils/swal.js';

export default {
  name: 'LineBroadcastDetail',
  setup() {
    const { pageTitle } = usePageTitle('推播活動詳情');
    return { pageTitle };
  },
  data() {
    return {
      loading: false,
      error: null,
      broadcast: null,
      failedBatches: [],
      retrying: false,
      insight: null,
      fetchingInsight: false
    };
  },
  computed: {
    broadcastFailedCount() {
      return this.failedBatches.reduce((sum, b) => sum + (b.batchSize || 0), 0);
    },
    broadcastPendingCount() {
      if (!this.broadcast) return 0;
      const sent = this.broadcast.actualSentCount || 0;
      const failed = this.broadcastFailedCount;
      const target = this.broadcast.targetCount || 0;
      return Math.max(0, target - sent - failed);
    },
    broadcastProgressPct() {
      if (!this.broadcast || !this.broadcast.targetCount) return 0;
      const pct = Math.round(((this.broadcast.actualSentCount || 0) / this.broadcast.targetCount) * 100);
      return Math.min(100, pct);
    },
    retryableBatches() {
      return this.failedBatches.filter(b => !b.errorMessage || !b.errorMessage.includes('403')).length;
    },
    insightDeliveryRate() {
      if (!this.insight?.delivered || !this.broadcast?.actualSentCount) return '0.0';
      return ((this.insight.delivered / this.broadcast.actualSentCount) * 100).toFixed(1);
    },
    insightOpenRate() {
      if (!this.insight?.opened || !this.insight?.delivered) return '0.0';
      return ((this.insight.opened / this.insight.delivered) * 100).toFixed(1);
    },
    insightClickRate() {
      if (!this.insight?.clicked || !this.insight?.delivered) return '0.0';
      return ((this.insight.clicked / this.insight.delivered) * 100).toFixed(1);
    }
  },
  mounted() {
    this.loadBroadcast();
  },
  methods: {
    async loadBroadcast() {
      this.loading = true;
      this.error = null;

      try {
        const response = await lineBroadcastService.getBroadcast(this.$route.params.id);
        
        if (response && response.success && response.data) {
          this.broadcast = response.data;
          if (['sending', 'completed', 'failed'].includes(this.broadcast.status)) {
            this.loadFailedBatches();
          }
          // 若 DB 已有上次查詢的 insight 數據，直接帶入，不需重新查詢
          if (this.broadcast.insightFetchedAt) {
            this.insight = {
              status: 'ready',
              delivered: this.broadcast.insightDelivered,
              opened:    this.broadcast.insightOpened,
              clicked:   this.broadcast.insightClicked,
              fetchedAt: this.broadcast.insightFetchedAt
            };
          }
        } else {
          this.error = response?.message || '獲取推播詳情失敗';
        }
      } catch (err) {
        console.error('載入推播詳情失敗:', err);
        this.error = err.response?.data?.message || err.message || '獲取推播詳情失敗';
      } finally {
        this.loading = false;
      }
    },

    async loadFailedBatches() {
      try {
        const response = await lineBroadcastService.getBroadcastBatches(this.$route.params.id, { status: 'failed' });
        if (response && response.success && response.data) {
          this.failedBatches = response.data || [];
        }
      } catch (err) {
        console.error('載入失敗批次失敗:', err);
      }
    },

    async handleFetchInsight() {
      this.fetchingInsight = true;
      this.insight = null;
      try {
        const response = await lineBroadcastService.fetchInsight(this.$route.params.id);
        if (response && response.success) {
          this.insight = response.data;
          if (response.data.status === 'unready') {
            this.$message?.warning?.('LINE Insight 資料尚未就緒，通常需要等到隔天才有資料');
          } else if (response.data.status === 'unsupported') {
            this.$message?.warning?.('此 LINE 官方帳號不支援 Insight API（僅認證帳號或優質帳號可用）');
          }
        }
      } catch (err) {
        await showError('查詢失敗', err.response?.data?.message || err.message || '查詢 LINE Insight 失敗');
      } finally {
        this.fetchingInsight = false;
      }
    },

    async handleRetryFailed() {
      const status = this.broadcast?.status;
      const isStuck = status === 'sending' || (status === 'completed' && !this.broadcast?.actualSentCount);
      const retryable = this.retryableBatches;
      const blocked = this.failedBatches.length - retryable;
      const blockedNote = blocked > 0 ? `\n（${blocked} 個因封鎖/拒絕的批次不在重試範圍）` : '';

      const confirmMsg = isStuck
        ? '推播可能因系統異常而未實際發送，將重新觸發所有批次重新發送。\n\n重試後訊息將在數分鐘內陸續送出。'
        : `將重新發送 ${retryable} 個失敗批次，共約 ${retryable * 500} 人（實際人數以批次記錄為準）。${blockedNote}\n\n重試後訊息將在數分鐘內陸續送出。`;

      const confirmed = await showConfirm(
        isStuck ? '確認重新發送' : '確認重試失敗批次',
        confirmMsg,
        '確認重試'
      );
      if (!confirmed) return;

      this.retrying = true;
      try {
        const response = await lineBroadcastService.retryFailedBatches(this.$route.params.id);
        if (response && response.success) {
          const { retriedBatches } = response.data;
          await showSuccess('重試成功', `已重新發送 ${retriedBatches} 個批次，請稍後重新整理查看結果`);
          this.loadBroadcast();
        } else {
          await showError('重試失敗', response?.message || '重試操作失敗');
        }
      } catch (err) {
        await showError('重試失敗', err.response?.data?.message || err.message || '重試操作失敗');
      } finally {
        this.retrying = false;
      }
    },

    handleBack() {
      this.$router.push({ name: 'ClientLineBroadcastList' });
    },

    async handleCancel() {
      if (!this.broadcast) return;

      const confirmed = await showConfirm(
        '確認取消推播',
        `確定要取消推播活動「${this.broadcast.campaignName}」嗎？此操作無法復原。`
      );
      
      if (!confirmed) return;

      this.loading = true;
      try {
        const response = await lineBroadcastService.cancelBroadcast(this.broadcast.id);
        if (response && response.success) {
          await showSuccess('取消成功', '推播活動已取消');
          this.loadBroadcast();
        } else {
          await showError('取消失敗', response?.message || '取消推播活動失敗');
        }
      } catch (err) {
        console.error('取消推播失敗:', err);
        await showError('取消失敗', err.response?.data?.message || err.message || '取消推播活動失敗');
      } finally {
        this.loading = false;
      }
    },

    formatMessageType(type) {
      const typeMap = {
        'text': '文字',
        'flex': 'Flex',
        'image': '圖片',
        'video': '影片',
        'audio': '音訊',
        'location': '位置',
        'sticker': '貼圖',
        'imagemap': '圖片地圖',
        'template': '範本',
        'carousel': '輪播'
      };
      return typeMap[type] || type || '-';
    },

    formatAudienceType(type) {
      const typeMap = {
        'members': '手動選擇',
        'tags': '標籤篩選',
        'rule': '受眾規則',
        'segment': '受眾群組',
        'custom_rule': '自訂規則',
        'all': '全部好友廣播'
      };
      return typeMap[type] || type || '-';
    },

    formatStatus(status) {
      const statusMap = {
        'draft': '草稿',
        'scheduled': '已排程',
        'preparing': '準備受眾',
        'sending': '發送中',
        'completed': '已完成',
        'failed': '失敗',
        'cancelled': '已取消'
      };
      return statusMap[status] || status || '-';
    },

    getStatusBadgeClass(status) {
      const classMap = {
        'draft': 'badge badge-secondary',
        'scheduled': 'badge badge-info',
        'preparing': 'badge badge-warning',
        'sending': 'badge badge-warning',
        'completed': 'badge badge-success',
        'failed': 'badge badge-danger',
        'cancelled': 'badge badge-secondary'
      };
      return classMap[status] || 'badge badge-secondary';
    },

    formatDate(dateString) {
      if (!dateString) return '-';
      return new Date(dateString).toLocaleString('zh-TW', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit'
      });
    }
  }
};
</script>

<style scoped>
@import '~@/assets/scss/components/_list-page-common.scss';

.detail-container {
  max-width: 1200px;
  margin: 0 auto;
}

.detail-section {
  background: #fff;
  border-radius: 8px;
  padding: 24px;
  margin-bottom: 24px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
}

.detail-section h3 {
  margin: 0 0 20px 0;
  font-size: 18px;
  font-weight: 600;
  color: #333;
  border-bottom: 2px solid #eee;
  padding-bottom: 12px;
}

.info-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 20px;
}

.info-item {
  display: flex;
  flex-direction: column;
}

.info-item label {
  font-size: 12px;
  color: #666;
  margin-bottom: 4px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.info-value {
  font-size: 14px;
  color: #333;
  font-weight: 500;
}

.prepare-error {
  margin: 8px 0 0;
  color: #b42318;
  font-size: 13px;
  font-weight: 400;
}

.message-preview {
  background: #f8f9fa;
  border-radius: 4px;
  padding: 16px;
  border: 1px solid #e9ecef;
}

.message-preview pre {
  margin: 0;
  white-space: pre-wrap;
  word-wrap: break-word;
  font-family: 'Courier New', monospace;
  font-size: 14px;
  color: #333;
}

.stats-section-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 16px;

  h3 {
    margin: 0;
  }
}

.insight-inline-actions {
  display: flex;
  align-items: center;
  gap: 12px;
}

.insight-fetched-time {
  font-size: 12px;
  color: #888;
  display: flex;
  align-items: center;
  gap: 4px;
}

.stat-sub {
  font-size: 12px;
  color: #888;
  margin-top: 2px;
}

.stats-row-label {
  font-size: 12px;
  font-weight: 600;
  color: #888;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin-bottom: 8px;

  &.insight-row-label {
    margin-top: 16px;
    padding-top: 14px;
    border-top: 1px dashed #d0e8ff;
    color: #2980b9;
  }
}

.insight-stats-row {
  margin-top: 0;
}

.insight-status-row {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-top: 12px;
  padding: 10px 14px;
  border-radius: 6px;
  font-size: 13px;
  color: #888;
  background: #fafafa;
  border: 1px dashed #ddd;

  &.unready  { color: #e67e22; background: #fff8f0; border-color: #f5cba7; }
  &.unsupported { color: #c0392b; background: #fff5f5; border-color: #f5c6cb; }
}

.stat-card.insight-stat {
  border-left: 3px solid #d0e8ff;
  background: #f8fbff;
}

.stat-icon.delivered { background: #e8f4fd; color: #2980b9; }
.stat-icon.opened    { background: #e8f8e8; color: #27ae60; }
.stat-icon.clicked   { background: #f3e8fd; color: #8e44ad; }

.stats-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 16px;
}

.stat-card {
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 16px;
  background: #f8f9fa;
  border-radius: 8px;
  border: 1px solid #e9ecef;
}

.stat-icon {
  width: 48px;
  height: 48px;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 24px;
  color: #fff;
}

.stat-icon.primary {
  background: #3498db;
}

.stat-icon.success {
  background: #2ecc71;
}

.stat-icon.info {
  background: #17a2b8;
}

.stat-icon.danger {
  background: #e74c3c;
}

.stat-icon.warning {
  background: #f39c12;
}

.stat-icon.secondary {
  background: #6c757d;
}

.stat-content {
  flex: 1;
}

.stat-label {
  font-size: 12px;
  color: #666;
  margin-bottom: 4px;
}

.stat-value {
  font-size: 24px;
  font-weight: 600;
  color: #333;
}

/* 進度條 */
.progress-bar-wrapper {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-top: 16px;
}

.progress-bar-track {
  flex: 1;
  height: 10px;
  background: #e9ecef;
  border-radius: 5px;
  overflow: hidden;
}

.progress-bar-fill {
  height: 100%;
  border-radius: 5px;
  transition: width 0.4s ease;
}

.progress-bar-fill.fill-success { background: #2ecc71; }
.progress-bar-fill.fill-danger  { background: #e74c3c; }

.progress-label {
  font-size: 13px;
  color: #666;
  white-space: nowrap;
}

/* 失敗批次 */
.failed-batches {
  margin-top: 24px;
  border: 1px solid #f5c6cb;
  border-radius: 8px;
  overflow: hidden;
}

.failed-batches-header {
  background: #fff5f5;
  padding: 12px 16px;
  border-bottom: 1px solid #f5c6cb;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
}

.failed-batches-header h4 {
  margin: 0;
  font-size: 14px;
  font-weight: 600;
  color: #c0392b;
  display: flex;
  align-items: center;
  gap: 6px;
}

@keyframes spin {
  from { transform: rotate(0deg); }
  to   { transform: rotate(360deg); }
}

.spinning {
  display: inline-block;
  animation: spin 0.8s linear infinite;
}

.table-wrapper {
  overflow-x: auto;
}

.table-wrapper table {
  width: 100%;
  border-collapse: collapse;
  font-size: 13px;
}

.table-wrapper th {
  background: #fafafa;
  padding: 10px 14px;
  text-align: left;
  font-weight: 600;
  color: #555;
  border-bottom: 1px solid #eee;
}

.table-wrapper td {
  padding: 10px 14px;
  border-bottom: 1px solid #f0f0f0;
  vertical-align: middle;
}

.table-wrapper tr:last-child td {
  border-bottom: none;
}

.error-text {
  color: #c0392b;
  display: flex;
  align-items: center;
  gap: 4px;
}

.error-text.retrying {
  color: #e67e22;
}

.text-muted {
  color: #888;
  font-size: 12px;
}

.hint-text {
  margin: 0;
  padding: 10px 16px;
  font-size: 12px;
  color: #888;
  background: #fafafa;
  border-top: 1px solid #eee;
  display: flex;
  align-items: center;
  gap: 4px;
}

.no-errors {
  margin-top: 16px;
  padding: 12px 16px;
  background: #f0faf5;
  border: 1px solid #c3e6cb;
  border-radius: 8px;
  color: #27ae60;
  font-size: 14px;
  display: flex;
  align-items: center;
  gap: 6px;
}

/* Insight 成效 */
.insight-section {}

.insight-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 16px;
}

.insight-header h3 { margin: 0 0 4px 0; }

.insight-note {
  margin: 0;
  font-size: 12px;
  color: #888;
  display: flex;
  align-items: center;
  gap: 4px;
}

.insight-account-note {
  margin-top: 16px;
  display: flex;
  align-items: flex-start;
  gap: 8px;
  padding: 10px 14px;
  margin-bottom: 16px;
  background: #f0f4ff;
  border: 1px solid #c5d3f5;
  border-radius: 6px;
  font-size: 13px;
  color: #4a5568;
  line-height: 1.5;

  i {
    color: #5a74c5;
    font-size: 15px;
    flex-shrink: 0;
    margin-top: 2px;
  }

  strong {
    color: #3451b2;
  }
}

.insight-empty,
.insight-loading,
.insight-unready,
.insight-unsupported {
  padding: 24px;
  text-align: center;
  color: #888;
  background: #fafafa;
  border: 1px dashed #ddd;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  font-size: 14px;
}

.insight-unready {
  color: #e67e22;
  background: #fff8f0;
  border-color: #f5cba7;
}

.insight-unsupported {
  color: #c0392b;
  background: #fff5f5;
  border-color: #f5c6cb;
  text-align: left;
  justify-content: flex-start;
  align-items: flex-start;

  i {
    font-size: 20px;
    flex-shrink: 0;
    margin-top: 2px;
  }

  p {
    margin: 4px 0 0;
    font-size: 13px;
    color: #6b2727;
    line-height: 1.5;
  }
}

.insight-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
  gap: 16px;
}

.insight-card {
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 16px;
  background: #f8f9fa;
  border-radius: 8px;
  border: 1px solid #e9ecef;
}

.insight-icon {
  width: 44px;
  height: 44px;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 20px;
  color: #fff;
  flex-shrink: 0;
}

.insight-icon.delivered { background: #3498db; }
.insight-icon.opened    { background: #2ecc71; }
.insight-icon.clicked   { background: #9b59b6; }

.insight-body { flex: 1; }

.insight-label {
  font-size: 12px;
  color: #666;
  margin-bottom: 2px;
}

.insight-value {
  font-size: 22px;
  font-weight: 600;
  color: #333;
  line-height: 1.2;
}

.insight-rate {
  font-size: 12px;
  color: #888;
  margin-top: 2px;
}

.insight-fetched-at {
  grid-column: 1 / -1;
  font-size: 12px;
  color: #aaa;
  text-align: right;
}
</style>
